


import { type ThemeOptions, type PaletteMode, type Theme } from '@mui/material'
import { navHover, navPressed, navRestText, navSelected } from './navChrome'
import { menuPaperChromeSx } from './menuChrome'
import { createTheme, alpha } from '@mui/material/styles'

import { normalize } from './normalize'
import { checkBoxBlankIcon } from './checkboxIcon'
import { breakpoints } from './breakpoints'
// import * as components from '../components'
import { palette } from './palette'
import { shadows } from './shadows'
import { shape } from './shape'
import { spacing } from './spacing'
import { typography } from './typography'
import type {} from '@mui/x-charts-pro/themeAugmentation';


export const options: ThemeOptions = {
  breakpoints,
  shape,
  palette,
  ...shadows,
  typography,
  ...spacing,
  components: {
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    /**
     * Skeletons are QUIETER than MUI's default, which was too heavy here.
     *
     * MUI derives the bar from `alpha(text.primary, mode === 'light' ? 0.11 : 0.13)`.
     * That assumes a mid-grey page; against this app's near-black `#09090b` (and the
     * near-white `#fafafa` text it tints from) 0.13 lands as a solid grey block, and a
     * table full of them reads as CONTENT — a wall of filled bars competing with the
     * real rows around it — rather than as an absence waiting to be filled.
     *
     * A skeleton's job is to hold the shape and say "not yet". It should be the
     * quietest thing on screen, because it is the only thing there that carries no
     * information. Halved to 0.06 (dark) / 0.055 (light) — still clearly above the
     * surface it sits on, no longer competing with text.
     *
     * The wave gets the same treatment: MUI sweeps `action.hover` across the bar, which
     * at the default alpha is a bright band travelling over an already-strong grey. Two
     * loud signals for one idea. The shimmer is what communicates "loading", so it keeps
     * slightly more contrast than the bar it crosses — but nothing like the default.
     */
    MuiSkeleton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'light' ? 0.055 : 0.06,
          ),
        }),
        wave: ({ theme }: { theme: Theme }) => ({
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'light' ? 0.05 : 0.055,
            )}, transparent)`,
          },
        }),
      },
    },
    MuiCssBaseline: {
      // Append a mode-aware body color/background AFTER the normalize reset so inherited
      // text (e.g. color:inherit buttons) follows the theme instead of the browser-default
      // black. The reset no longer hardcodes body color.
      styleOverrides: (t: Theme) =>
        `${normalize}\n  body { color: ${t.palette.text.primary}; background-color: ${t.palette.background.default}; }\n` +
        // VRG-227 — MUI-X date/time pickers (`@mui/x-date-pickers[-pro]`). `PickersPopper`
        // renders `<Popper><PickersPopperPaperWrapper PaperComponent={Paper} /></Popper>` —
        // same shape as `MuiTooltip` above: `.MuiPickersPopper-paper` is a CHILD of the
        // transform-carrying Popper root, never the node itself, so zooming it directly is
        // safe (confirmed by reading `@mui/x-date-pickers/internals/components/PickersPopper.js`).
        // Raw CSS here rather than a typed `MuiPickersPopper` theme component key: that key
        // needs `@mui/x-date-pickers[-pro]/themeAugmentation`'s module augmentation, which
        // does NOT resolve from `packages/ui` (no direct dependency, and — unlike
        // `@mui/x-charts-pro`, whose augmentation happens to resolve via this monorepo's
        // pnpm hoisting — it isn't reachable here; confirmed by an actual `tsc` run, not
        // assumed). A global CSS rule needs no such resolution.
        `  .MuiPickersPopper-paper { zoom: var(--text-zoom, 1); }`,
    },
    // VRG-227 — restores visual zoom-scaling for EVERY Popover-family surface
    // (Popover, Menu, Select's menu, ActionDropdown, Sort, and every raw
    // `<Popover>` call site — e.g. the Filters drawer's per-field dropdowns) in
    // ONE place, without touching the paper's own position.
    //
    // `MuiMenu`'s Paper is `styled(PopoverPaper, {name:'MuiMenu', slot:'Paper'})`
    // (`@mui/material/Menu/Menu.js`) — emotion's styled-wrapping composes BOTH
    // classes onto the same rendered node, so a `MuiPopover.styleOverrides.paper`
    // override reaches Menu's paper too, confirmed by reading the source (not
    // assumed). That single override point is what makes this "one place" cover
    // every consumer, Menu-based or not.
    //
    // `zoom` MUST target `& > *` (the paper's rendered children), never the paper
    // element itself. `useTextZoom`'s docblock has the full empirical proof;
    // short version: this Paper is the SAME DOM node MUI sets `style.top`/
    // `style.left` on (`Popover.js` `getPositioningStyle`/`setPositioningStyles`,
    // computed from the anchor's real `getBoundingClientRect()`). Zooming the
    // paper itself re-scales that already-correct offset a second time —
    // reproduced empirically at every tested zoom level (0.9/1/1.2/1.4), where
    // the resulting error was EXACTLY `offset × zoom` — while zooming a CHILD of
    // the paper (this rule) leaves the paper's own position untouched (proven:
    // zero px delta at every level) and still scales everything a user actually
    // reads (menu item text, icons, row padding/height).
    //
    // Known, accepted gap: the paper's OWN chrome — this 4px inset (`MENU_PAPER_SX`,
    // call-site-applied) and the 12px radius/border/shadow below (this file) —
    // does not itself scale, since it is declared on the (deliberately unzoomed)
    // paper, not the child. Moving it too would mean stripping ALL visual styling
    // off `.MuiMenu-paper`/`.MuiPopover-paper` into a new inner wrapper and
    // reconciling every call site's own `slotProps.paper.sx` (MENU_PAPER_SX,
    // `filterDropdownPaperSx`, etc.) against it — a materially larger, higher-
    // regression-risk change than this ticket's reported bug (mispositioned
    // popovers) warrants. The dominant visual signal of "this got bigger" — text,
    // icons, row height — already scales correctly; a few px of outer inset/
    // radius not scaling is not perceptible as "this didn't zoom".
    MuiPopover: {
      styleOverrides: {
        paper: {
          '& > *': {
            zoom: 'var(--text-zoom, 1)',
          },
        },
      },
    },
    /**
     * A placeholder must never read as a TYPED VALUE.
     *
     * MUI's default renders the placeholder in the input's OWN colour at ~0.42
     * opacity, and this theme states no rule of its own — so on the dark surface
     * both measured `rgb(250,250,250)`, separated only by opacity. On a near-black
     * field that still reads as bright text: a "Lovelace" placeholder in Last name
     * looked entered next to a genuinely-typed "S" in First name, which is what
     * prompted this.
     *
     * Mode-aware rather than a single literal: the same alpha that reads as "hint"
     * on a dark field reads as "disabled" on a light one. Sign-in and other auth
     * pages hand-rolled this per-field before it lived here; those overrides are now
     * redundant, and any new field gets the correct treatment for free.
     */
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }: { theme: Theme }) => ({
          '&::placeholder': {
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.38)'
              : 'rgba(0, 0, 0, 0.42)',
            opacity: 1,
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        // Kill MUI's dark-mode elevation overlay (grey glow) on every dropdown globally.
        // backgroundImage: 'none' removes it; border + shadow give clean definition.
        //
        // ⚠️ The VALUES now live in `menuChrome`'s `menuPaperChromeSx`, not here, and
        // this is the only reason that constant exists: `styleOverrides` is keyed on
        // the COMPONENT NAME, so this block reaches `<Menu>` and nothing else. A
        // popover-style surface that is not a Menu — `MuiAutocomplete.paper` below, the
        // analytics metric-inputs `<Popover>` — has to spread the constant itself, and
        // before it existed each one either re-typed these four properties or (the
        // Popover) simply floated with the overlay still on. Reading from one source is
        // what keeps them in step.
        //
        // One shadow weight for both modes. Light was briefly raised to shadows[6] on the
        // theory that light has no fill headroom left (page-to-white is only 1.092:1) so
        // elevation had to carry the separation. Rejected on sight — a heavy drop shadow
        // reads as consumer-SaaS chrome, and this console's register is closer to an auction
        // catalogue than to a dashboard.
        //
        // The real defect was never the elevation, it was the EDGE: `divider` at
        // rgba(0,0,0,0.12) is #e0e0e0 on the white paper, which is 1.315:1 against its own
        // fill but only 1.204:1 against the PAGE it overlaps — under the ~1.3:1 a line needs
        // to read. The shadow was compensating for a border that does not register. If this
        // wants fixing, raise the border alpha (0.20 measures 1.470:1), do not add elevation.
        paper: ({ theme }: { theme: Theme }) => menuPaperChromeSx(theme),
        // House list padding so items breathe + the rounded item highlight has room.
        list: { padding: '4px' },
      },
    },
    // House default for EVERY menu item (raw MUI Menus, ActionDropdown kebabs, etc.):
    // comfortable 44px height, rounded highlight, brand-purple hover + brand-tinted
    // selected. Components that self-style their items (SelectField, @repo/ui Select)
    // override this locally, so they're unaffected. Centralised so cramped/grey menus
    // don't recur.
    // An Autocomplete's list is a menu to the user, but MUI styles its rows through
    // MuiAutocomplete.option rather than MuiMenuItem — so the MenuItem override below never
    // reached them, and the country picker was highlighting rows with a neutral
    // rgba(109,109,109,0.12) while every other dropdown used the brand tint. Same nav language,
    // one source, so the three dropdown families cannot drift apart again.
    //
    // Note the state names differ from a menu's: `Mui-focused` is the keyboard/pointer cursor
    // (hover), and `aria-selected` is the chosen value (selected).
    // Every ListItemButton in this app is a nav or menu row -- the six sidebar components
    // (main nav, Help, Reader, Search, Collapse, nested list) plus SearchSelect's option list.
    // Verified by enumerating the 7 files that use it, so this override cannot leak into an
    // unrelated list.
    //
    // It exists because Help and "Reader not connected" declare no hover of their own and so
    // fell through to MUI's default `action.hover` -- a neutral grey -- while the rows directly
    // above them had been moved to the brand tint. Patching each component would have left the
    // next sidebar row to be added with the same bug; one override is where the rule belongs.
    MuiListItemButton: {
      styleOverrides: {
        /*
          A row RESTS DIM and BRIGHTENS on hover — the same treatment the ⌘K palette
          uses, extended to the sidebar and every other nav list.

          The point is movement: a list where every row is already at full contrast
          has nothing left to say when the cursor arrives, so the only feedback is a
          faint background wash. Dropping the resting state to `text.secondary` gives
          the hover somewhere to go, and the row under the cursor becomes the only
          bright text in the list — which is what makes a long nav feel responsive
          rather than static.

          Contrast is not sacrificed to get it, but it took a mode-aware token to
          keep that true. `text.secondary` is #a1a1aa in BOTH modes: 7.3:1 on the
          dark sidebar and **2.46:1** on the light one (#fafafa) — under WCAG AA.
          Measured in the browser in light mode, which is the only reason it was
          caught. `navRestText` returns #52525b in light (~7.5:1) instead.

          SELECTED stays bright. It is the answer to "where am I", which must not
          depend on where the pointer happens to be, and it already carries the
          brand-tinted background.

          And selected text stays NEUTRAL-bright — `text.primary`, never the brand
          purple the background is tinted with. Recorded here because "the selected
          row should be purple, it already has a purple pill" is the obvious next
          suggestion, and it loses on contrast. Measured in the browser at 1280x800
          against each mode's real sidebar surface (VRG-537):

            brand[500] #8f68ea on light #fafafa    3.770:1   <- under AA's 4.5
            brand[500] #8f68ea on dark  #18181b    4.502:1   <- ON the line, +0.002
            text.primary, composited              15.520:1 light, 16.970:1 dark

          Light fails outright and dark passes by two thousandths, which is not a
          margin — any future nudge to the sidebar surface or the brand ramp takes
          it under without anyone noticing.

          The redundancy argument seals it: selection is ALREADY stated twice, by
          the icon and by the `navSelected` pill behind the row. Tinting the label
          is a third signal bought by dropping 16px body text to the AA floor, which
          is a bad trade even when the signal is wanted. Verified 2026-09-01: no
          sidebar nav label renders chromatic in either mode.

          `color` on the button, not on the label, so the ICON inherits it too —
          lucide glyphs stroke with `currentColor`, so icon and text brighten
          together as one row rather than the text moving alone.
        */
        root: ({ theme }: { theme: Theme }) => ({
          color: navRestText(theme),
          transition: 'color 0.12s ease, background-color 0.12s ease',
          '&:hover': {
            backgroundColor: navHover(theme),
            color: theme.palette.text.primary,
          },
          /*
            PRESSED — one perceptible step past hover, no further. See `navPressed`.

            Without this the press fell through to MUI's default ripple, a dark splash
            sized for Material's much heavier surfaces. On this app's near-white lists it
            read as SELECTED (a persistent, brand-tinted state) rather than as a 120ms
            press, so the two momentarily looked alike.

            Declared AFTER `:hover` deliberately — equal specificity, so source order
            decides, and a pressed row must win while the pointer is necessarily also
            over it.
          */
          '&:active': {
            backgroundColor: navPressed(theme),
          },
          '&.Mui-selected': {
            backgroundColor: navSelected(theme),
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: navSelected(theme),
              color: theme.palette.text.primary,
            },
            /*
              A selected row does NOT deepen on press. It already carries the strongest
              surface in the list, and darkening it further would be a state change with
              nothing on the other side of it — the row is already where you are.
            */
            '&:active': {
              backgroundColor: navSelected(theme),
            },
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // The Autocomplete popup is a PAPER, but it is not a `MuiMenu`, so the
        // `MuiMenu.paper` override above never reached it. Every Select-style
        // dropdown in the app carried a 1px divider edge except the Autocomplete
        // ones (country picker, the multi-selects), which floated borderless —
        // most visible on the dark signup surface, where a borderless dark paper
        // has nothing separating it from the dark page behind it.
        //
        // Stated here rather than per-component so every Autocomplete gets it at
        // once, and so it stays in step with MuiMenu if that edge ever changes.
        // `backgroundImage: none` kills MUI's dark-mode elevation overlay for the
        // same reason it is killed on MuiMenu.
        //
        // ⚠️ DELIBERATELY A SUBSET of `menuChrome`'s `menuPaperChromeSx`, not a call to
        // it. This takes the two properties that were actually wrong (overlay, edge) and
        // leaves the Autocomplete popup on its own elevation and radius. Adopting the
        // full chrome here would move the shadow and the corner on every Autocomplete in
        // both apps — a real visual change to the country picker and the multi-selects,
        // which is a decision to take on its own evidence rather than as a side effect of
        // extracting a constant. If that is ever done, replace this body with
        // `menuPaperChromeSx(theme)` and verify those surfaces.
        paper: ({ theme }: { theme: Theme }) => ({
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
        }),
        // The LISTBOX slot, not the `option` slot. Styling `option` looks like the obvious
        // choice and loses: MUI's own listbox default is
        // `.MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"].Mui-focused`,
        // which carries an extra attribute selector and therefore outranks anything written on
        // the option slot alone. Overriding from the same slot matches its specificity and
        // lands after it. (Diagnosed by enumerating every matching background rule on a live
        // highlighted option, not by reading the docs — the option-slot version measured as
        // applied-but-losing.)
        listbox: ({ theme }: { theme: Theme }) => ({
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: navHover(theme),
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: navSelected(theme),
          },
          '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
            backgroundColor: navSelected(theme),
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          minHeight: 44,
          paddingTop: 8,
          paddingBottom: 8,
          borderRadius: 8,
          fontSize: 14,
          // Same rest-dim / hover-bright treatment as the nav lists above, so a
          // dropdown and the sidebar it opens from behave identically. Menu rows
          // previously rendered at full white at rest, which is why an earlier
          // attempt to "brighten on hover" in ActionDropdown did nothing at all —
          // there was no headroom to brighten into.
          color: navRestText(theme),
          transition: 'color 0.12s ease, background-color 0.12s ease',
          '&:hover': {
            backgroundColor: navHover(theme),
            color: theme.palette.text.primary,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.brand?.[600] || '#7F48DF', 0.12),
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: navSelected(theme),
          },
        }),
      },
    },
    MuiTooltip: {
      // No-arrow + a small enter delay by default (shadcn-like; the triangle is off).
      defaultProps: {
        arrow: false,
        enterDelay: 200,
      },
      // Theme-aware surface: WHITE in light mode (dark text), DARK in dark mode
      // (light text) — a dark box on a light page reads heavy, so light mode gets a
      // white card with a hairline border + shadow, matching the app's popovers/menus.
      // (MUI's default greys can't be used here: this theme inverts the grey ramp in
      // dark mode, so the default resolves to a light box + white text = unreadable.)
      styleOverrides: {
        tooltip: ({ theme }: { theme: Theme }) => {
          const dark = theme.palette.mode === 'dark'
          return {
            backgroundColor: dark ? '#141417' : '#ffffff',
            color: dark ? '#fafafa' : theme.palette.text.primary,
            // VRG-545: 14 → 15. Tooltip text is read at the moment of confusion, by
            // someone who has stopped to ask what something means, and it was the
            // smallest type on the screen. Bumped rather than left to each call site,
            // because a per-site fix is how it drifted to 12 in InfoTooltip.
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1.5,
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
            boxShadow: theme.shadows[3],
            // VRG-227 — SAFE to zoom the full chrome here (unlike MuiPopover/MuiMenu's
            // paper above): Tooltip positions via Popper.js's `transform: translate3d()`
            // applied to the OUTER `.MuiTooltip-popper` root; `.MuiTooltip-tooltip` (this
            // element) is a separate CHILD MUI renders inside it
            // (`@mui/material/Tooltip/Tooltip.js` — `<PopperComponent><TooltipComponent>`),
            // never the node the transform lands on. So the whole visible box — bg,
            // border, radius, shadow, text — scales correctly with zero position risk.
            zoom: 'var(--text-zoom, 1)',
          }
        },
        arrow: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.mode === 'dark' ? '#141417' : '#ffffff',
        }),
      },
    },
    // VRG-227 — Drawer/Dialog portal into `document.body` by default too (same
    // as Popover/Menu — `Modal`'s `Portal` fallback), so once `document.body` is
    // no longer the zoomed element (see `useTextZoom.ts`), their content needs
    // the same explicit re-zoom Popover/Menu/Tooltip get above. UNLIKE Popover's
    // paper, it is safe to zoom `.MuiDrawer-paper`/`.MuiDialog-paper` DIRECTLY
    // (not just a child): both position via plain static CSS with no runtime
    // anchor-rect math — Drawer's paper is `position:fixed; top/left/right/
    // bottom:0` edge-docking (`@mui/material/Drawer/Drawer.js`), Dialog's is
    // `position:relative` inside a flex-centered container
    // (`@mui/material/Dialog/Dialog.js`) — so there is no already-real-pixel
    // offset for a self-applied zoom to double.
    //
    // ⚠️ TEMPORARY drawers ONLY. The re-zoom is correct only for a paper that sits
    // OUTSIDE `#app`, and only `variant="temporary"` (the default) portals. `permanent`
    // and `persistent` render inline — `Drawer.js` returns `<DrawerDockedRoot>{paper}`
    // with no `Modal`/`Portal` — so their paper is already inside `#app`'s zoom, and a
    // second `zoom` on it multiplies: the paper paints at width × z² while its docked
    // column (same width, zoomed once) paints at width × z. That was the platform
    // sidebar (`Sidebar.tsx`, the app's one permanent drawer). Measured at 1440×900:
    // column 230.4px vs paper 184.3px at z=0.8 (a 46px blank strip between the rail
    // and the page), and 345.6 vs 414.7 at z=1.2 (the rail painted 69px over the page).
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }: { ownerState: { variant?: string } }) =>
          ownerState.variant === 'permanent' || ownerState.variant === 'persistent'
            ? {}
            : { zoom: 'var(--text-zoom, 1)' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          zoom: 'var(--text-zoom, 1)',
        },
      },
    },
    // MUI-X date/time pickers: see the `.MuiPickersPopper-paper` raw CSS rule in
    // `MuiCssBaseline.styleOverrides` above (a typed `MuiPickersPopper` theme key needs
    // module augmentation that doesn't resolve from this package — see that rule's comment).
    MuiIconButton: {
      // House style: icon buttons are rounded-SQUARES, not full circles. MUI defaults
      // IconButton to border-radius 50%; pin a square-ish radius to match the Button /
      // Paper / input radii across the app. Applies app-wide (shared theme).
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCheckbox: {
      // The ONE place a checkbox's box is defined, for both apps. Unchecked box shape +
      // colour and checked colour all live here; do NOT re-specify them per call site
      // (`Table.tsx` used to, and drifted — see below).
      defaultProps: {
        // VRG-474 — a 1px STROKED rounded square, replacing MUI's `CheckBoxOutlineBlank`.
        // MUI's blank icon is a FILLED path whose ring is 2 viewBox units thick, i.e. a
        // 2px border at the default 24px icon size, and `Table.tsx` painted a `stroke` on
        // that same path, which straddles both contours and took the rendered thickness to
        // 3px. Measured in the browser on the Certificates list. A field border in this app
        // is 1px (`fieldChrome.fieldBorderColor`); a checkbox had no business being 3x that.
        icon: checkBoxBlankIcon,
      },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          // Mode-aware. This forced common.white in BOTH modes ("white checkbox app-wide"),
          // which is only ever right on a dark surface — in light mode a white checkbox sits
          // white-on-white, so an unchecked box showed a barely-there outline and a CHECKED box
          // was a white fill with a white tick.
          //
          // UNCHECKED and CHECKED are now different values, which they were not before. The
          // split is not decoration: it is what a column of checkboxes needs to be readable at
          // a glance — an empty box is chrome, a ticked one is data, and at one shared colour
          // the two read as equally loud. `Table.tsx` had discovered this and encoded it
          // per-site (0.4 light / 0.55 dark for unchecked); that lives here now so modal and
          // list checkboxes get it too, and there is one place to change it.
          //
          // LIGHT unchecked #8b8b93 = 3.4:1 on white, 3.1:1 on the #f6f6f6 field fill. That is
          // the floor, not a preference: WCAG 1.4.11 wants >=3:1 for a non-text UI component,
          // and an unchecked checkbox is defined ENTIRELY by its border — there is no fill to
          // carry the shape, which is why it cannot simply adopt the 1.24:1 `primary[100]` that
          // bordered FIELDS use. #a1a1aa fails outright at 2.6:1; do not go lighter.
          //
          // It reads much lighter than the 4.8:1 #71717a it replaces even so, because the
          // BORDER also went 3px -> 1px in the same change (see `defaultProps.icon`). Perceived
          // weight is ink area x contrast, so a third of the ink at 1.19x the contrast is a net
          // ~40% of what was on screen before.
          color: theme.palette.mode === 'light' ? '#8b8b93' : 'rgba(255, 255, 255, 0.7)',
          // Checked keeps the older, heavier value in light and stays pure white in dark, so a
          // ticked box is unambiguously the loudest thing in the column. Dark unchecked is 0.7
          // rather than the 0.55 `Table.tsx` used, because 0.55 was tuned against a 3px border;
          // at 1px the same alpha reads as barely-there on the near-black surface.
          '&.Mui-checked': {
            color: theme.palette.mode === 'light' ? '#71717a' : theme.palette.common.white,
          },
          '&.MuiCheckbox-indeterminate': {
            color: theme.palette.mode === 'light' ? '#71717a' : theme.palette.common.white,
          },
        }),
      },
    },
    // MuiButton: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 8,
    //     },
    //   },
    // },
    // MuiListItemButton: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 8,
    //     },
    //   },
    // },
    // MuiPaper: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 8,
    //     },
    //   },
    // },
    // MuiCard: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 8,
    //     },
    //   },
    // },
    // MuiTextField: {
    //   styleOverrides: {
    //     root: {
    //       '& .MuiOutlinedInput-root': {
    //         borderRadius: 8,
    //       },
    //     },
    //   },
    // },
    // MuiAutocomplete: {
    //   styleOverrides: {
    //     root: {
    //       '.MuiFilledInput-root': {
    //         paddingTop: 0,
    //         paddingLeft: 8,
    //         paddingRight: 8,
    //       },
    //       '.MuiInputBase-input': {
    //         paddingTop: 0,
    //       },
    //       '&.MuiAutocomplete-hasPopupIcon .MuiFilledInput-root, &.MuiAutocomplete-hasClearIcon .MuiFilledInput-root': {
    //         paddingRight: 8,
    //       },
    //     },
    //     inputRoot: {
    //       paddingTop: 0,
    //     },
    //     paper: {
    //       borderRadius: 8,
    //     },
    //   },
    // },
    // ...components,
  },
}

export const theme = createTheme(options)

/**
 * Build the app theme for a given palette mode.
 *
 * `light` uses the canonical palette as-is. `dark` sets `palette.mode = 'dark'`
 * so MUI recomputes its mode-aware semantic slots (text / background / divider /
 * action) for dark surfaces while the brand + scale tokens carry over.
 *
 * NOTE (dark-mode fidelity): components that reference ABSOLUTE palette tokens
 * directly (e.g. `palette.black['87%']`, `palette.white`, `palette.gray[…]`)
 * will NOT flip automatically — only MUI's semantic slots do. Full dark fidelity
 * requires migrating those usages to mode-aware semantic tokens, which rides the
 * per-epic component work (see ADR 0002) and the Figma dark tokens once the
 * Dev-Mode MCP is enabled. This factory delivers the infrastructure + correct
 * MUI semantics; per-component dark polish is incremental.
 */
// Best-judgment dark theme (no Figma dark tokens exist yet). The brand neutral ramps
// (primary/gray/grey) are fixed light values, so `mode: 'dark'` alone leaves surfaces
// light. Remap the neutral ramps so the light end (surfaces) goes dark and the dark end
// (text/strong) goes light, plus explicit dark `background` + light `text`. Brand and
// semantic hues are unchanged. The all-black/all-white scales are left for incremental
// follow-up (per-component, ADR 0002).
// shadcn "zinc" dark ramp: brand neutral scales run light→dark by index (50 lightest),
// so in dark mode we reverse them onto the zinc scale — low index → dark surface,
// high index → light text.
const darkNeutral = {
  25: '#09090b',  // zinc-950 (deepest)
  50: '#18181b',  // zinc-900 (base surface)
  100: '#27272a', // zinc-800 (muted / border)
  200: '#3f3f46', // zinc-700
  300: '#52525b', // zinc-600
  400: '#71717a', // zinc-500 (muted icons)
  500: '#a1a1aa', // zinc-400 (muted text)
  600: '#d4d4d8', // zinc-300
  700: '#e4e4e7', // zinc-200
  800: '#f4f4f5', // zinc-100
  900: '#fafafa', // zinc-50 (text)
  950: '#ffffff',
}

// In dark mode the all-black scale is used for text/icons/dividers → flip solids to
// light and the black-alpha overlays to white-alpha (same opacity). The all-white
// scale is used for solid surfaces → flip solids to a dark surface, keeping the
// white-alpha overlays (subtle light highlights read correctly on dark).
const darkBlack = {
  ...(palette.black as object),
  50: '#fafafa', 100: '#fafafa', 200: '#fafafa', 300: '#fafafa', 400: '#fafafa',
  500: '#fafafa', 600: '#fafafa', 700: '#fafafa', 800: '#fafafa', 900: '#fafafa', 950: '#fafafa',
  '100%': 'hsla(0, 0%, 100%, 1)',
  '87%': 'hsla(0, 0%, 100%, 0.87)',
  '72%': 'hsla(0, 0%, 100%, 0.72)',
  '52%': 'hsla(0, 0%, 100%, 0.52)',
  '32%': 'hsla(0, 0%, 100%, 0.32)',
  '24%': 'hsla(0, 0%, 100%, 0.24)',
  '16%': 'hsla(0, 0%, 100%, 0.16)',
  '12%': 'hsla(0, 0%, 100%, 0.12)',
  '6%': 'hsla(0, 0%, 100%, 0.06)',
  '4%': 'hsla(0, 0%, 100%, 0.04)',
  '2%': 'hsla(0, 0%, 100%, 0.02)',
}
const darkWhite = {
  ...(palette.white as object),
  50: '#18181b', 100: '#27272a', 200: '#27272a', 300: '#3f3f46', 400: '#18181b',
  500: '#18181b', 600: '#18181b', 700: '#18181b', 800: '#18181b', 900: '#18181b', 950: '#18181b',
}

const darkPalette = {
  ...palette,
  mode: 'dark' as const,
  primary: { ...(palette.primary as object), ...darkNeutral },
  gray: { ...(palette.gray as object), ...darkNeutral },
  grey: { ...(palette.grey as object), ...darkNeutral },
  black: darkBlack,
  white: darkWhite,
  // VERA-816: cards (paper) sit a step above the near-black page so they read as
  // surfaces without glowing. Applies to every card app-wide.
  background: { default: '#09090b', paper: '#0f0f12' },
  text: { primary: '#fafafa', secondary: '#a1a1aa', disabled: '#52525b' },
  // VRG-800 — see the light-mode twin below. 0.12 measures 1.33:1 against #09090b, which
  // is AT the ~1.3:1 floor at full coverage and drops straight through it the moment a
  // browser zoom makes the 1px border sub-pixel (0.5 device px of a 12% colour composites
  // to ~1.13:1 — invisible). 0.20 takes it to 1.75:1 with the same headroom at reduced
  // coverage. Reported as the tab-row rule thinning out on zoom-out; the 2px active
  // indicator was unaffected, which is what isolated it to the border width.
  divider: 'rgba(255, 255, 255, 0.20)',
} as typeof palette

export function createAppTheme(mode: PaletteMode): Theme {
  return createTheme({
    ...options,
    palette:
      mode === 'dark'
        ? darkPalette
        : {
            ...palette,
            mode,
            // The PAGE is tinted and the PANEL is white — not the other way round.
            //
            // This used to be `default: #ffffff, paper: #fafafa`, on the reasoning that a
            // card wanted "the slightest grey… to be faintly distinct from the white page".
            // That inverts the ramp: it makes a panel DARKER than the page, so it reads as a
            // smudge rather than a raised surface, and it spends the only direction with any
            // room — white is the ceiling, so a panel on a white page cannot go lighter.
            // Measured, the step was 1.044:1 and the panels were effectively invisible.
            //
            // Now a panel is lighter than its page in BOTH modes (dark: #0f0f12 on #09090b,
            // light: #ffffff on #f4f6f6), so "lighter means nearer" is one rule rather than
            // two contradictory ones, and the fill works WITH the 1px border instead of
            // against it. It also fixes the step below: an input on a white panel reads as a
            // recess, which is what a field should look like.
            // HEX, not the `hsla(180 5% 96%)` this was first written as, even though they
            // are the same colour. MUI's colour utilities decompose and recompose these
            // tokens (DataGrid blends its pinned-cell background with the row-hover overlay,
            // for one), and the hsla string came back out as `rgb(173, 5, 92)` — magenta —
            // on every hovered pinned cell. Same value in a format the maths handles.
            background: { default: '#f4f5f5', paper: '#ffffff' },
            // VRG-800 — `divider` had never been assigned in this repo, so every border in
            // both apps was MUI's default rgba(0,0,0,0.12). The MuiMenu comment above
            // (~line 181) already measured that exact value as 1.204:1 against the page —
            // "under the ~1.3:1 a line needs to read" — and recommended 0.20, which it
            // measured at 1.470:1. That recommendation sat unacted-on, and the shadow was
            // compensating for a border that does not register. Acting on it here rather
            // than at the one call site, because `divider` is the single source for every
            // border in the app and fixing it per-surface is what produced the drift §69
            // and §31 both document.
            divider: 'rgba(0, 0, 0, 0.20)',
          },
  })
}

export const lightTheme = createAppTheme('light')
export const darkTheme = createAppTheme('dark')